(() => {
  'use strict';

  const roots = [
    ...document.querySelectorAll('section[id^="vc-clarity-"]'),
    ...document.querySelectorAll('section[id^="VCSteps-"]'),
    ...document.querySelectorAll('section[id^="VCAthletes-"]'),
    ...document.querySelectorAll('section[id^="vc-real-training-"]'),
    ...document.querySelectorAll('section[id^="vc-knowledge-editorial-"]')
  ];

  if (!roots.length) return;

  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const registered = new Set();

  const imageReady = (img) => new Promise((resolve) => {
    const done = () => {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => {}).finally(resolve);
      } else {
        resolve();
      }
    };

    if (img.complete) {
      done();
      return;
    }

    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', resolve, { once: true });
  });

  const warmImages = (node) => {
    const images = [...node.querySelectorAll('img')];
    images.forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
      try { img.fetchPriority = 'auto'; } catch (_) {}
    });
    return images.map(imageReady);
  };

  const revealWithImages = async (node) => {
    if (node.classList.contains('vgp-visible')) return;

    const pending = warmImages(node);
    if (pending.length) {
      await Promise.race([
        Promise.allSettled(pending),
        new Promise((resolve) => setTimeout(resolve, 900))
      ]);
    }

    requestAnimationFrame(() => node.classList.add('vgp-visible'));
  };

  const revealObserver = reduced || !('IntersectionObserver' in window)
    ? null
    : new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealWithImages(entry.target);
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -7% 0px' });

  const warmObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          warmImages(entry.target);
          warmObserver.unobserve(entry.target);
        });
      }, { threshold: 0, rootMargin: '900px 0px 900px 0px' })
    : null;

  const addReveal = (node, index = 0, card = false) => {
    if (!node || registered.has(node)) return;
    registered.add(node);

    node.classList.add('vgp-reveal');
    if (card) node.classList.add('vgp-reveal--card');
    node.style.setProperty('--vgp-delay', `${Math.min(index * 80, 240)}ms`);

    if (reduced || !revealObserver) {
      node.classList.add('vgp-visible');
    } else {
      revealObserver.observe(node);
    }
  };

  roots.forEach((root) => {
    root.classList.add('vgp-polished');
    warmObserver?.observe(root);

    if (root.id.startsWith('vc-clarity-')) {
      addReveal(root.querySelector('.vc-clarity-v2__header'));
      root.querySelectorAll('.vc-clarity-v2__card').forEach((el, i) => addReveal(el, i, true));
      return;
    }

    if (root.id.startsWith('VCSteps-')) {
      addReveal(root.querySelector('.vc-steps__header'));
      root.querySelectorAll('.vc-step').forEach((el, i) => addReveal(el, i, true));
      return;
    }

    if (root.id.startsWith('VCAthletes-')) {
      addReveal(root.querySelector('.vc-athletes__intro'));
      root.querySelectorAll('.vc-athletes__card').forEach((el, i) => addReveal(el, i, true));
      return;
    }

    if (root.id.startsWith('vc-real-training-')) {
      addReveal(root.querySelector('.vc-rtm__copy'));
      const all = root.querySelector('.vc-rtm__all');
      if (all) addReveal(all, 1);
      return;
    }

    if (root.id.startsWith('vc-knowledge-editorial-')) {
      addReveal(root.querySelector('.vc-ke__header'));
      root.querySelectorAll('.vc-ke__card').forEach((el, i) => addReveal(el, i, true));
    }
  });

  /* Theme editor can re-render a section without a full page load. */
  document.addEventListener('shopify:section:load', () => {
    window.setTimeout(() => {
      document.querySelectorAll('.vgp-reveal:not(.vgp-visible)').forEach((el) => {
        if (reduced) el.classList.add('vgp-visible');
      });
    }, 0);
  });
})();
